import { QuestionCircleOutlined } from '@ant-design/icons';
import {
  omitUntouchedMeasurements,
  useMeasurementUnit,
  useMeasurementUnitReady,
  useRefillOnUnitSwitch,
} from 'hooks/useMeasurementUnit';
import { withMeasurementUnitScope } from 'hooks/useMeasurementUnitScope';
import { RevxUnitSelect } from 'components/RevxUnitSelect';
import { inViewerUnit } from 'utils/measurement';
import {
  Affix,
  Divider,
  Form,
  Input,
  PageHeader,
  Popconfirm,
  Switch,
} from 'antd';
import { useAppDispatch, useAppSelector } from 'app/hooks';
import { ArrowLeftIcon } from 'components/Icons';
import { RevxButton } from 'components/RevxButton';
import RevxFlexLayout from 'components/RevxFlexLayout';
import { RevxSpin } from 'components/RevxSpin';
import { Headline } from 'components/RevxTypography';
import { isPlatformOrg } from 'domains/orgDomain';
import { SvcParameter, SvcSpec } from 'domains/svcDomain';
import { useOrgId } from 'hooks/useOrg';
import { cloneDeep, isEmpty, isUndefined } from 'lodash-es';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ADMIN_DETAIL_AFFIX_TOP,
  PLATFORM_FORM_ITEM_LAYOUT,
} from 'utils/constants';
import { MeasureType } from 'domains/measurementDomain';
import { RevxMeasureTextInput } from 'components/RevxMeasureInput/RevxMeasureTextInput';
import {
  RULE_INTEGER,
  RULE_MEASURE_PRECISION,
  RULE_PRECISION,
  RULE_TEXT_LEN_255,
} from 'utils/validators';

const SVCSpecItemForm = () => {
  const { t } = useTranslation();

  const [form] = Form.useForm();

  const { unit, tag } = useMeasurementUnit();
  // Held until the org's system is known - see useMeasurementUnitReady. Opening this form in the
  // gap would label every measurement field with a unit that is about to change.
  const unitReady = useMeasurementUnitReady();
  const dispatch = useAppDispatch();
  const saving = useAppSelector(
    (state) => state.loading.effects.svc.createBaseSpec
  );
  const deleting = useAppSelector(
    (state) => state.loading.effects.svc.deleteSpec
  );
  const { curSpec, viewingSvcSpec, creatingSvcSpec } = useAppSelector(
    (state) => state.svc
  );
  const specFormData = useMemo(() => {
    if (isUndefined(curSpec?.parameters) || isEmpty(curSpec?.parameters))
      return curSpec;
    let paramFields: { [key: string]: string } = {};
    curSpec!.parameters.forEach((p) => {
      paramFields['param-' + p.label] = p.parameter;
    });
    return { ...curSpec, ...paramFields };
  }, [curSpec]);
  const svcData = useAppSelector((state) => state.svc.data);
  const curSvc = useMemo(() => {
    return svcData?.find((svc) => svc.id === curSpec?.svcId);
  }, [curSpec?.svcId, svcData]);

  const orgId = useOrgId();
  const isBase = isPlatformOrg({ id: orgId });

  const onClose = () => {
    dispatch.svc.setState({
      curSpec: undefined,
      creatingSvcSpec: false,
      viewingSvcSpec: false,
    });
  };
  const onSave = async () => {
    let values;
    try {
      values = await form.validateFields();
    } catch (e: any) {
      const firstErrField = document.querySelector(
        `#${e.errorFields[0].name[0]}`
      );
      if (firstErrField?.scrollIntoView) {
        setTimeout(() => {
          firstErrField?.scrollIntoView();
        }, 1000);
      }

      return;
    }

    const parameters = buildParameters(curSpec, values);

    if (curSpec?.id) {
      await dispatch.svc.updateSpec(
        tag(
          omitUntouchedMeasurements(
            { ...curSpec, ...values, parameters },
            form
          )
        )
      );
    } else {
      await dispatch.svc.createBaseSpec(
        tag(
          omitUntouchedMeasurements(
            { ...curSpec, ...values, parameters },
            form
          )
        )
      );
    }

    onClose();
  };

  const onRemove = async () => {
    if (curSpec?.id && curSpec?.svcId) {
      await dispatch.svc.deleteSpec({
        svcId: curSpec.svcId,
        specId: curSpec.id,
      });
      onClose();
    }
  };

  const actionBar = (
    <div>
      <Affix offsetTop={ADMIN_DETAIL_AFFIX_TOP}>
        <PageHeader
          className="platform-item-form-header"
          onBack={() => {
            onClose();
          }}
          backIcon={<ArrowLeftIcon />}
          title={
            <Headline>
              {curSpec?.id
                ? t('platform.svc.form.title.editSpec')
                : t('platform.svc.form.title.createSpec')}
            </Headline>
          }
          subTitle={<RevxUnitSelect />}
          extra={[
            <RevxButton type="primary" brandGreen onClick={onSave}>
              {t('common.save')}
            </RevxButton>,
            <>
              <Divider type="vertical" />
              <RevxButton onClick={onClose} brandGreen>
                {t('common.cancel')}
              </RevxButton>
            </>,
            ...(curSpec?.id
              ? [
                  <>
                    <Divider type="vertical" />
                    <Popconfirm
                      title={t('common.confirmQuestion')}
                      icon={<QuestionCircleOutlined style={{ color: 'red' }} />}
                      onConfirm={onRemove}
                      okText={t('common.ok')}
                      cancelText={t('common.cancel')}
                      overlayStyle={{ width: 160 }}
                      getPopupContainer={(e) => {
                        return e as HTMLElement;
                      }}
                    >
                      <RevxButton danger>{t('common.remove')}</RevxButton>
                    </Popconfirm>
                  </>,
                ]
              : []),
          ]}
        ></PageHeader>
      </Affix>
    </div>
  );

  useRefillOnUnitSwitch(form, specFormData);

  return (
    <RevxSpin spinning={saving || deleting || !unitReady}>
      {actionBar}
      <div className="platform-item-form-wrapper">
        <Form
          {...PLATFORM_FORM_ITEM_LAYOUT}
          className="platform-item-form"
          layout={'horizontal'}
          form={form}
          scrollToFirstError
          initialValues={{
            ...inViewerUnit(specFormData, unit),
          }}
        >
          <Form.Item
            label={t('common.status')}
            name="active"
            valuePropName="checked"
          >
            <Switch disabled={creatingSvcSpec} />
          </Form.Item>
          <Form.Item
            label={t('common.revxId')}
            name="revxId"
            rules={[RULE_INTEGER, { required: true }]}
          >
            <Input disabled={viewingSvcSpec} />
          </Form.Item>
          <Form.Item
            label={t('common.description')}
            name="description"
            rules={[RULE_TEXT_LEN_255, { required: true }]}
          >
            <Input disabled={!isBase} />
          </Form.Item>

          {!isEmpty(curSpec?.parameters) && (
            <Form.Item label={t('platform.svc.form.parameters')}>
              {curSpec?.parameters?.map((parameter) => (
                <Form.Item>
                  <Input.Group>
                    <span className="ant-input-group-addon">
                      {parameter.label}
                    </span>

                    <Form.Item
                      name={'param-' + parameter.label}
                      noStyle
                      rules={[RULE_TEXT_LEN_255]}
                    >
                      <Input disabled={!isBase} />
                    </Form.Item>
                  </Input.Group>
                </Form.Item>
              ))}
            </Form.Item>
          )}

          <Form.Item
            label={t('platform.svc.tableColumn.costUsd')}
            name="costUsd"
            rules={[RULE_PRECISION(18), { required: false }]}
          >
            <Input prefix={t('common.dollarSign')} />
          </Form.Item>
          <Form.Item
            label={t('platform.svc.tableColumn.makereadyCost')}
            name="makereadyCostUsd"
            rules={[RULE_PRECISION(15), { required: false }]}
          >
            <Input prefix={t('common.dollarSign')} />
          </Form.Item>
          <Form.Item
            label={t('platform.svc.tableColumn.markupPct')}
            name="markupPct"
            rules={[RULE_PRECISION(2), { required: false }]}
          >
            <Input />
          </Form.Item>

          <div style={{ display: curSvc?.hasMaterial ? 'block' : 'none' }}>
            <Form.Item label={t('platform.svc.tableColumn.serviceMaterial')}>
              <RevxFlexLayout justify="space-between" align="top">
                <Form.Item
                  name="materialCostUsd"
                  rules={[RULE_PRECISION(21)]}
                  style={{
                    display: 'inline-block',
                    width: 'calc(50% - 12px)',
                  }}
                >
                  <Input
                    addonBefore={t('platform.svc.tableColumn.materialCost')}
                    prefix={'$'}
                  />
                </Form.Item>
                <Form.Item
                  name="materialMarkupPct"
                  rules={[RULE_PRECISION(2)]}
                  style={{
                    display: 'inline-block',
                    width: 'calc(50% - 12px)',
                  }}
                >
                  <Input
                    addonBefore={t(
                      'platform.svc.tableColumn.materialMarkupPct'
                    )}
                  />
                </Form.Item>
              </RevxFlexLayout>
              <RevxFlexLayout justify="space-between" align="top">
                <Form.Item
                  name="materialWidthIn"
                  rules={[RULE_MEASURE_PRECISION(5, MeasureType.LENGTH, form)]}
                  style={{
                    display: 'inline-block',
                    width: 'calc(50% - 12px)',
                  }}
                >
                  <RevxMeasureTextInput
                    type={MeasureType.LENGTH}
                    addonBefore={t('common.width')}
                  />
                </Form.Item>
                <Form.Item
                  name="materialLengthIn"
                  rules={[RULE_MEASURE_PRECISION(5, MeasureType.LENGTH, form)]}
                  style={{
                    display: 'inline-block',
                    width: 'calc(50% - 12px)',
                  }}
                >
                  <RevxMeasureTextInput
                    type={MeasureType.LENGTH}
                    addonBefore={t('common.length')}
                  />
                </Form.Item>
              </RevxFlexLayout>
              <RevxFlexLayout justify="space-between" align="top">
                <Form.Item
                  name="materialHeightIn"
                  rules={[RULE_MEASURE_PRECISION(5, MeasureType.LENGTH, form)]}
                  style={{
                    display: 'inline-block',
                    width: 'calc(50% - 12px)',
                  }}
                >
                  <RevxMeasureTextInput
                    type={MeasureType.LENGTH}
                    addonBefore={t('common.height')}
                  />
                </Form.Item>
                <Form.Item
                  name="materialThicknessIn"
                  rules={[RULE_MEASURE_PRECISION(5, MeasureType.CALIPER, form)]}
                  style={{
                    display: 'inline-block',
                    width: 'calc(50% - 12px)',
                    marginBottom: 0,
                  }}
                >
                  <RevxMeasureTextInput
                    type={MeasureType.CALIPER}
                    addonBefore={t('common.thickness')}
                  />
                </Form.Item>
              </RevxFlexLayout>
              <RevxFlexLayout justify="space-between" align="top">
                <Form.Item
                  name="maxTotalCaliperIn"
                  rules={[RULE_MEASURE_PRECISION(5, MeasureType.CALIPER, form)]}
                  style={{
                    display: 'inline-block',
                    width: 'calc(50% - 12px)',
                    marginBottom: 0,
                  }}
                >
                  <RevxMeasureTextInput
                    type={MeasureType.CALIPER}
                    addonBefore={t(
                      'platform.svc.tableColumn.maxTotalCaliperIn'
                    )}
                  />
                </Form.Item>
              </RevxFlexLayout>
            </Form.Item>
          </div>

          <Form.Item
            label={t('platform.svc.tableColumn.timeHr')}
            name="timeHr"
            rules={[RULE_PRECISION(8)]}
          >
            <Input />
          </Form.Item>
          <Form.Item
            label={t('platform.svc.tableColumn.makereadyTimeHr')}
            name="makereadyTimeHr"
            rules={[RULE_PRECISION(5)]}
          >
            <Input />
          </Form.Item>
          <Form.Item
            label={t('platform.svc.tableColumn.leadTimeHr')}
            name="leadTimeHr"
            rules={[RULE_PRECISION(2)]}
          >
            <Input />
          </Form.Item>
        </Form>
      </div>
    </RevxSpin>
  );
};
export default withMeasurementUnitScope(SVCSpecItemForm);

function buildParameters(
  curSpec: Partial<SvcSpec> | undefined,
  values: any
): SvcParameter[] {
  if (!isUndefined(curSpec!.parameters) && !isEmpty(curSpec!.parameters)) {
    let parameters = cloneDeep(curSpec!.parameters);
    for (let k in values) {
      if (k.startsWith('param-')) {
        let param = parameters.find((p) => p.label === k.substring(6));
        if (param) param.parameter = values[k];
      }
    }
    return parameters;
  }
  return [];
}
